import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import type { PendingUserResponse } from '@/lib/api/data-contracts';

/**
 * 가입 승인 요청 본문.
 * 생성된 ApproveUserRequest DTO에는 아직 roleSetId가 없어 별도 정의한다.
 * (백엔드 계약: orgCd 필수, roleSetId 필수, compCd 선택)
 */
export interface ApproveUserBody {
  compCd?: string;
  orgCd: string;
  roleSetId: number;
}

export function usePendingUsers() {
  const [users, setUsers] = useState<PendingUserResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPendingUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('admin/user/pending')
        .json<{ result?: PendingUserResponse[] }>();
      setUsers(res.result ?? []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPendingUsers(); }, [fetchPendingUsers]);

  const approveUser = async (userId: number, request: ApproveUserBody) => {
    await apiClient.put(`admin/user/${userId}/approve`, { json: request }).json();
    await fetchPendingUsers();
  };

  const rejectUser = async (userId: number) => {
    await apiClient.put(`admin/user/${userId}/reject`).json();
    await fetchPendingUsers();
  };

  return { users, loading, refetch: fetchPendingUsers, approveUser, rejectUser };
}
